import React, { useEffect, useRef, useState } from "react";
import "./TradingTerminal.css";
import ExchangeNavBar from "../widgets/ExchangeNavBar";
import TradingChartBox from "../partials/unifiedLayout/TradingChartBox";
import TradeBox from "../partials/unifiedLayout/TradeBox";
import WatchlistPanel from "../partials/unifiedLayout/WatchlistPanel";
import socketIOClient from "socket.io-client";
import { updatePrice } from "../../redux/marketSlice";
import { imageUrl, socketUrl } from "../constant/BaseUrl";
import { useDispatch, useSelector } from "react-redux";
import {
  addOrder,
  cancelOrder,
  fillOrder,
  setClosedOrders,
} from "../../redux/ordersSlice";
import {
  addPosition,
  closePosition,
  setPositions,
  updatePosition,
} from "../../redux/positionsSlice";
import {
  useCloseTradesPnlQuery,
  useGetBalanceQuery,
  useGetClosedPositionsQuery,
  useGetUserPositionsQuery,
} from "../../redux/exchangeUniApi";
import UserTicker from "../partials/UserTicker";
import { useTranslation } from "react-i18next";
import {
  updateOpenPnl,
  setAccountBalance,
  updateClosedPnl,
} from "../../redux/accountUniSlice";
import toast from "react-hot-toast";
import { showOrderNotification } from "../partials/unifiedLayout/showOrderNotification";
import { useGetSelectedLeverageQuery } from "../../redux/forexApi";
import { addNotification } from "../../redux/notificationsSlice";

const UnifiedLayout = () => {
  const { t } = useTranslation();
  const [theme, setTheme] = useState("dark");
  const [userLeverage, setUserLeverage] = useState(1);
  const account = useSelector((state) => state.account);
  const userData = useSelector((state) => state?.user?.userData);
  const selectedSymbol = useSelector((state) => state.watchlist.selectedSymbol);
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const [isOrderBoxShow, setIsOrderBoxShow] = useState(false);
  const [stepSize, setStepSize] = useState(0.00001);
  // const [screenSize, setScreenSize] = useState("tv_chart_container2-lower");
  const [screenSize, setScreenSize] = useState("tv_chart_container2");
  const [existingOrder, setExistingOrder] = useState(null);
  const positions = useSelector((state) => state.positions.data);
  const { data: accountData, refetch: refetchBalance } = useGetBalanceQuery(
    userData?.id
  );
  const { data: selectedLeverages } = useGetSelectedLeverageQuery();
  const { data: closedPnl, refetch: refetchClosePnl } = useCloseTradesPnlQuery(
    userData?.id
  );
  const { data: closedPositions, refetch: refetchClosePositions } =
    useGetClosedPositionsQuery(userData?.id);

  useEffect(() => {
    if (selectedLeverages) {
      if (selectedLeverages) {
        const cryptoLeverage = selectedLeverages.global_leverage || 1;
        setUserLeverage(cryptoLeverage);
      } else {
        setUserLeverage(1);
      }
    }
  }, [selectedLeverages]);

  useEffect(() => {
    if (closedPnl !== undefined) {
      dispatch(updateClosedPnl(closedPnl));
    }
  }, [closedPnl]);

  useEffect(() => {
    if (closedPositions) {
      dispatch(setClosedOrders(closedPositions));
    }
  }, [closedPositions, dispatch]);

  const { data: openPositions, refetch } = useGetUserPositionsQuery(
    userData?.id
  );
  const livePrices = useSelector((state) => state.market.prices);

  useEffect(() => {
    if (accountData) {
      dispatch(setAccountBalance(accountData)); // balance, usedMargin, available
    }
  }, [accountData, dispatch]);

  useEffect(() => {
    if (!userData?.id || !userData?.refred_by_id) return;

    const socket = socketIOClient(socketUrl, {
      transports: ["websocket"],
      query: {
        token: localStorage.getItem("jwtToken"),
      },
      reconnection: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("🟢 Connected to WebSocket");

      socket.emit("subscribe_watchlist", {
        userId: userData.id,
        brokerId: userData.refred_by_id,
      });

      socket.emit("subscribe_user", userData.id);
    });

    socket.on("marketData", (data) => {
      // console.log(data, "price");
      dispatch(updatePrice(data));
      // handleMarketUpdate(data);
    });

    socket.on("user_event", (data) => {
      // console.log(data, "from user_events");

      if (data.type === "notifications") {
        dispatch(addNotification(data));
      }

      if (data.type === "order_placed") {
        dispatch(addOrder({ type: "order_placed", payload: data }));
        showOrderNotification(data);
      }
      if (data.type === "order_filled") {
        // dispatch(addOrder({ type: "order_filled", payload: data }));
        console.log(data, "postion open");
        dispatch(fillOrder({ order_id: data.order_id }));
        dispatch(
          addPosition({
            id: data.position_id,
            symbol: data.symbol,
            entry_price: data.entry_price,
            quantity: data.quantity,
            side: data.side === "BUY" ? "long" : "short",
            status: "open",
            stop_loss: data.stop_loss,
            take_profit: data.take_profit,
            created_at: new Date().toISOString(),
            segment: data.segment,
            fee: data.fee,
          })
        );
        showOrderNotification(data);
      }

      if (data.type === "position_closed") {
        console.log(data, "data");
        dispatch(addOrder({ type: "position_closed", payload: data }));
        dispatch(closePosition(data.position_id));
        showOrderNotification(data);
        dispatch(updateClosedPnl(parseFloat(data.pnl)));
      }

      if (data.type === "order_cancelled") {
        dispatch(addOrder({ type: "order_cancelled", payload: data }));
        dispatch(cancelOrder(data.order_id));
        showOrderNotification(data);
      }

      if (data.type === "positions_updated") {
        dispatch(updatePosition(data));
        showOrderNotification(data);
      }

      if (data.type === "insufficient_balance") {
        toast.dismiss();
        toast.error(data.message);
      }
      refetchBalance();
    });

    socket.on("disconnect", () => {
      console.log("🔴 Disconnected from WebSocket");
    });

    return () => socket.disconnect();
  }, [userData?.id, userData?.refred_by_id, dispatch]);

  useEffect(() => {
    if (openPositions?.length) {
      dispatch(setPositions(openPositions));
    }
  }, [openPositions, dispatch]);

  function calculatePnlFrontend(pos, livePrice) {
    const entry = parseFloat(pos.entry_price);
    const qty = parseFloat(pos.quantity);
    const lev = parseFloat(pos.leverage || 1);
    const symbol = pos.symbol.toUpperCase();
    const segment = pos.segment || "crypto"; // fallback if missing
    const side = pos.side;

    const currentPrice = side === "long" ? livePrice.bid : livePrice.ask;
    const direction = side === "long" ? 1 : -1;

    let pnl = 0;

    if (segment === "forex") {
      const isJPY = symbol.endsWith("JPY");
      const isMetal = symbol.startsWith("XAU") || symbol.startsWith("XAG");
      const lots = qty; // assuming qty is in lots

      if (isMetal) {
        pnl = direction * (currentPrice - entry) * 100 * lots;
      } else if (isJPY) {
        pnl =
          (direction * (currentPrice - entry) * lots * 100000) / currentPrice;
      } else {
        pnl = direction * (currentPrice - entry) * 100000 * lots;
      }
    } else {
      // crypto, equity
      pnl =
        side === "long"
          ? (currentPrice - entry) * qty
          : (entry - currentPrice) * qty;
    }

    const fee = parseFloat(pos.fee);

    return pnl - fee;
  }

  useEffect(() => {
    let totalOpenPnl = 0;

    for (let pos of positions) {
      const live = livePrices[pos.symbol.toUpperCase()];
      if (!live) continue;

      const pnl = calculatePnlFrontend(pos, live);
      totalOpenPnl += pnl;
    }

    dispatch(updateOpenPnl(parseFloat(totalOpenPnl.toFixed(2))));
  }, [positions, livePrices]);

  const handleEditPositions = (pos) => {
    setExistingOrder(pos);
    setIsOrderBoxShow(true);
  };

  const changeChartTheme = (theme) => {
    if (theme === "defaultTheme" || theme === "darkTheme") {
      setTheme("dark");
    } else {
      setTheme("light");
    }
  };

  return (
    <div>
      <ExchangeNavBar changeChartTheme={changeChartTheme} unified={false} />

      <div className="new_dashbord ">
        <div key="topbar">
          <div className="bg2 kline-para kline-para-basic d-flex align-items-center border_box">
            <div className="box-kline-para-basic-left ">
              <span
                className="productSymbol has-info ng-scope"
                style={{ fontSize: "11px" }}
              >
                <strong className="ng-binding">{selectedSymbol}</strong>
                {/* <font className="ng-binding"> / USDT</font> */}
              </span>
            </div>
            <div className="box-kline-para-basic-right">
              <UserTicker
                translate={t}
                balance={account?.balance?.toFixed(2)}
                equityPrice={parseFloat(account?.equity)}
                leverage={userLeverage}
                usedMargin={account?.marginUsed?.toFixed(2)}
                freeMargin={account?.available?.toFixed(2)}
                profit={account?.totalPnl?.toFixed(2)}
              />
            </div>
          </div>
        </div>

        <div className="dash-grid">
          <div className={isOrderBoxShow ? "dash-grid-l" : "dash-grid-l-f"}>
            <TradingChartBox
              isOrderBoxShow={isOrderBoxShow}
              setIsOrderBoxShow={setIsOrderBoxShow}
              getExistingOrder={handleEditPositions}
              manageScreenSize={screenSize}
              setScreenSize={setScreenSize}
              theme={theme}
              stepSize={stepSize}
            />
          </div>
          <div className={isOrderBoxShow ? "dash-grid-r" : "dash-grid-r-f"}>
            <TradeBox
              isOrderBoxShow={isOrderBoxShow}
              setIsOrderBoxShow={setIsOrderBoxShow}
              existingOrder={existingOrder}
              getExistingOrder={setExistingOrder}
              leverage={userLeverage}
              stepSize={stepSize}
            />
            <WatchlistPanel setStepSize={setStepSize} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLayout;
